import { useMemo, useState } from 'react';

import { Typography } from '@material-ui/core';
import {
  Button,
  Dialog,
  DialogContent as DialogBody,
  DialogActions as DialogFooter,
  DialogTitle as DialogHeader,
  IconButton,
  Typography as Text,
  TextField,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { RiCloseLine } from '@remixicon/react';

import { useCheckResourcePermissions } from '@spotify/plugin-confidence-permissions-react';

import {
  useCanCreatePulse,
  useCapturePulseBlock,
  useCreatePulse,
  usePulses,
} from '../../hooks';
import {
  PULSE_BLOCK_TYPE,
  type PulseListItem,
  type SaveToPulseInput,
} from '../../types';
import { getPulseTitle, stripPulseVisualizationResultData } from '../../utils';
import { usePulseBlockCaptures } from '../PulseBlockCaptures';
import classes from './SaveToPulseDialog.module.css';

export type SaveToPulseSuccess = {
  pulseName: string;
  pulseDisplayName: string;
};

type SaveToPulseDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  input: SaveToPulseInput;
  onSuccess?: (info: SaveToPulseSuccess) => void;
};

type PulseMode = 'existing' | 'new';

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Unknown error';

const PulseLoadingState = () => (
  <Text variant="body2" color="textSecondary">
    Loading pulses...
  </Text>
);

export const SaveToPulseDialog = ({
  isOpen,
  onOpenChange,
  input,
  onSuccess,
}: SaveToPulseDialogProps) => {
  const { pulses, isLoading } = usePulses();
  const { capturePulseBlock, isMutating } = useCapturePulseBlock();
  const { createPulse, isCreating } = useCreatePulse();
  const { addCapture, projectName } = usePulseBlockCaptures();
  const { allowed: canCreatePulse } = useCanCreatePulse();

  const [mode, setMode] = useState<PulseMode>('existing');
  const [blockName, setBlockName] = useState(input.suggestedName);
  const [newPulseName, setNewPulseName] = useState('');
  const [createdPulseName, setCreatedPulseName] = useState<string | null>(null);
  const [selectedPulseName, setSelectedPulseName] = useState<string | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const pulseOptions = useMemo(
    () =>
      (pulses ?? []).map(pulse => ({
        value: pulse.name,
        label: getPulseTitle(pulse),
      })),
    [pulses],
  );

  const selectedPulse = useMemo<PulseListItem | null>(
    () =>
      (pulses ?? []).find(pulse => pulse.name === selectedPulseName) ?? null,
    [pulses, selectedPulseName],
  );
  const { allowed: canEditSelectedPulse, loading: isLoadingSelectedPulseEdit } =
    useCheckResourcePermissions({
      name: selectedPulseName ?? undefined,
      can: 'edit',
      optimistic: false,
    });

  const isSaving = isMutating || isCreating;
  const selectedMode = canCreatePulse ? mode : 'existing';

  const resetAndClose = () => {
    setErrorMessage(null);
    onOpenChange(false);
  };

  const getBlockDisplayName = () => blockName.trim() || input.suggestedName;

  const getCaptureVisualizationSpec = () =>
    input.blockType === PULSE_BLOCK_TYPE.CHART
      ? stripPulseVisualizationResultData(input.visualizationSpec)
      : input.visualizationSpec;

  const handleAddToExistingPulse = async () => {
    if (!selectedPulse) return;
    setErrorMessage(null);
    try {
      const result = await capturePulseBlock({
        sourceChat: input.sourceChat,
        sourceEvent: input.sourceEvent,
        displayName: getBlockDisplayName(),
        blockType: input.blockType,
        visualizationSpec: getCaptureVisualizationSpec(),
        selectedContent: input.selectedContent,
        pulse: selectedPulse.name,
      });
      if (result.block?.name) {
        addCapture({
          pulseName: selectedPulse.name,
          pulseDisplayName: selectedPulse.displayName,
          blockName: result.block.name,
          blockDisplayName: result.block.displayName || getBlockDisplayName(),
        });
      }
      onSuccess?.({
        pulseName: selectedPulse.name,
        pulseDisplayName: selectedPulse.displayName ?? '',
      });
      resetAndClose();
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  const handleCreateAndAdd = async () => {
    const displayName = newPulseName.trim();
    if (!displayName && !createdPulseName) return;

    setErrorMessage(null);
    try {
      const pulseName =
        createdPulseName ??
        (
          await createPulse(
            {
              displayName,
              ...(projectName ? { projects: [projectName] } : {}),
            },
            undefined,
          )
        ).name;
      setCreatedPulseName(pulseName);
      const result = await capturePulseBlock({
        sourceChat: input.sourceChat,
        sourceEvent: input.sourceEvent,
        displayName: getBlockDisplayName(),
        blockType: input.blockType,
        visualizationSpec: getCaptureVisualizationSpec(),
        selectedContent: input.selectedContent,
        pulse: pulseName,
      });
      if (result.block?.name) {
        addCapture({
          pulseName,
          pulseDisplayName: displayName,
          blockName: result.block.name,
          blockDisplayName: result.block.displayName || getBlockDisplayName(),
        });
      }
      onSuccess?.({ pulseName, pulseDisplayName: displayName });
      resetAndClose();
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  const handleNewPulseNameChange = (value: string) => {
    setNewPulseName(value);
    setCreatedPulseName(null);
  };

  const handlePulseSelectionChange = (
    _event: React.ChangeEvent<{}>,
    option: { value: string; label: string } | null,
  ) => {
    setSelectedPulseName(option?.value ?? null);
  };

  const hasBlockName = getBlockDisplayName().length > 0;
  const canAddToExistingPulse =
    selectedMode === 'existing' &&
    selectedPulse !== null &&
    canEditSelectedPulse &&
    hasBlockName &&
    !isSaving;
  const canCreateAndAdd =
    selectedMode === 'new' &&
    canCreatePulse &&
    newPulseName.trim().length > 0 &&
    hasBlockName &&
    !isSaving;
  const createAndAddButtonLabel = (() => {
    if (isSaving) return 'Adding...';
    if (createdPulseName) return 'Add to pulse';
    return 'Create and add';
  })();

  const pulseSelector = isLoading ? (
    <PulseLoadingState />
  ) : (
    <Autocomplete
      options={pulseOptions}
      getOptionLabel={option => option.label}
      getOptionSelected={(option, value) => option.value === value.value}
      value={pulseOptions.find(o => o.value === selectedPulseName) ?? null}
      onChange={handlePulseSelectionChange}
      openOnFocus
      renderInput={params => (
        <TextField
          {...params}
          label="Choose a pulse"
          placeholder="Search pulses"
        />
      )}
    />
  );

  return (
    <Dialog open={isOpen} onClose={() => onOpenChange(false)}>
      <DialogHeader>
        Add to pulse
        <IconButton
          aria-label="Close"
          onClick={() => onOpenChange(false)}
          style={{ position: 'absolute', right: 8, top: 8 }}
        >
          <RiCloseLine size={16} />
        </IconButton>
      </DialogHeader>
      <DialogBody className={classes.body}>
        <div className={classes.form}>
          <TextField
            id="save-to-pulse-block-name"
            label="Block name"
            value={blockName}
            onChange={e => setBlockName(e.target.value)}
            required
            error={!hasBlockName}
          />

          {input.selectedContent && (
            <div>
              <Typography variant="caption" color="textSecondary">
                Selected content
              </Typography>
              <div className={classes.selectedContentPreview}>
                <Typography
                  variant="body2"
                  className={classes.selectedContentText}
                >
                  {input.selectedContent}
                </Typography>
              </div>
            </div>
          )}

          <div className={classes.modeActions} aria-label="Pulse save mode">
            <Button
              variant={selectedMode === 'existing' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setMode('existing')}
            >
              Existing pulse
            </Button>
            {canCreatePulse && (
              <Button
                variant={selectedMode === 'new' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setMode('new')}
              >
                New pulse
              </Button>
            )}
          </div>

          {selectedMode === 'existing' && pulseSelector}

          {selectedMode === 'new' && (
            <TextField
              id="save-to-pulse-name"
              label="Pulse name"
              value={newPulseName}
              onChange={e => handleNewPulseNameChange(e.target.value)}
              placeholder="e.g. Streaming metrics"
            />
          )}

          {selectedMode === 'existing' &&
            selectedPulse &&
            !isLoadingSelectedPulseEdit &&
            !canEditSelectedPulse && (
              <Text
                component="p"
                variant="body2"
                color="textSecondary"
                className={classes.helpText}
              >
                You need edit access to add a block to this pulse.
              </Text>
            )}

          {selectedMode === 'existing' &&
            !isLoading &&
            pulseOptions.length === 0 && (
              <Text
                component="p"
                variant="body2"
                color="textSecondary"
                className={classes.helpText}
              >
                {canCreatePulse
                  ? 'Create a new pulse to add this block.'
                  : 'No pulses are available.'}
              </Text>
            )}

          {errorMessage && (
            <Text
              component="p"
              variant="body2"
              color="error"
              className={classes.helpText}
            >
              {errorMessage}
            </Text>
          )}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outlined" onClick={resetAndClose}>
          Cancel
        </Button>
        {selectedMode === 'existing' ? (
          <Button
            variant="contained"
            onClick={() => void handleAddToExistingPulse()}
            disabled={!canAddToExistingPulse}
            color="primary"
          >
            {isSaving ? 'Adding...' : 'Add to pulse'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => void handleCreateAndAdd()}
            disabled={!canCreateAndAdd}
            color="primary"
          >
            {createAndAddButtonLabel}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
};
